/**
 * Bootstraps Mandate Individual Space on module entry.
 * Ensures mandate data is loaded and the dedicated personal space is active.
 */

import { useEffect, useRef, useState } from 'react';
import { useMandateStore } from '../stores/mandate/mandateStore';
import { bootstrapMandateIndividualSpace } from '../lib/mandateBridgeService';
import { useUserStore } from '../stores/prioritization/userStore';
import { useSpaceStore } from '../stores/prioritization/spaceStore';

/**
 * Where the embedded platform pages stand:
 * - `loading`: the transition is still being read, or its space is being opened.
 * - `ready`: the mandate space is active in spaceStore; platform pages may render.
 * - `no-transition`: the person has no active transition, so there is no mandate
 *   space to show — the shell says so instead of waiting for one.
 * - `failed`: the transition exists but its space could not be opened.
 */
export type MandateBootstrapStatus = 'loading' | 'ready' | 'no-transition' | 'failed';

export interface MandateBootstrapState {
  /** True once the mandate space has been confirmed active in spaceStore. */
  isReady: boolean;
  /** Null until bootstrap completes; the resolved mandate space id. */
  mandateSpaceId: string | null;
  status: MandateBootstrapStatus;
}

export function useMandateSpaceBootstrap(): MandateBootstrapState {
  const { transition, fetchAll } = useMandateStore();
  const userId = useUserStore((s) => s.userId);
  const [isReady, setIsReady] = useState(false);
  const [mandateSpaceId, setMandateSpaceId] = useState<string | null>(null);
  const [transitionRead, setTransitionRead] = useState(false);
  const [bootstrapFailed, setBootstrapFailed] = useState(false);

  // Detect if the persisted currentSpaceId is already a mandate space on mount —
  // that allows instant readiness on subsequent visits without waiting for the RPC.
  const currentSpaceId = useSpaceStore((s) => s.currentSpaceId);
  const mySpaces = useSpaceStore((s) => s.mySpaces);
  useEffect(() => {
    if (!currentSpaceId) return;
    const space = mySpaces.find((s) => s.id === currentSpaceId);
    if (space && (space.settings as Record<string, unknown>)?.['originModule'] === 'mandate') {
      setMandateSpaceId(currentSpaceId);
      setIsReady(true);
    }
  }, [currentSpaceId, mySpaces]);

  // fetchAll settles whether or not a transition exists (and on a read error);
  // until it has, "no transition" is not yet known, only unloaded. It reads by
  // the signed-in person, so it runs again once the user is known: on a cold
  // load the first call can run before the session is established and find no
  // one.
  useEffect(() => {
    let cancelled = false;
    fetchAll().finally(() => {
      if (!cancelled) setTransitionRead(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchAll, userId]);

  // The space switcher's own bootstrap runs beside this one and, when its read
  // lands last, drops the mandate space for the workspace's default. Once it
  // has settled, select the mandate space again — once per settle, so a person
  // switching spaces by hand afterwards is not fought.
  const bootstrapStatus = useSpaceStore((s) => s.bootstrapStatus);
  useEffect(() => {
    if (bootstrapStatus !== 'ready' || !mandateSpaceId) return;
    const { currentSpaceId: current, mySpaces: spaces, setCurrentSpace } = useSpaceStore.getState();
    if (current === mandateSpaceId) return;
    if (spaces.some((s) => s.id === mandateSpaceId)) setCurrentSpace(mandateSpaceId);
  }, [bootstrapStatus, mandateSpaceId]);

  // Keyed on the transition's id, not the object: the bootstrap writes the
  // resolved space back onto the transition, and re-running on that write
  // called ensure_mandate_space again, and again, for as long as the page lived.
  const transitionId = transition?.id ?? null;
  const transitionRef = useRef(transition);
  transitionRef.current = transition;
  useEffect(() => {
    const current = transitionRef.current;
    if (!current || !userId) return;
    let cancelled = false;

    bootstrapMandateIndividualSpace(current, userId).then((boot) => {
      if (cancelled) return;
      if (boot.error) {
        console.error('[MandateBootstrap]', boot.error);
        setBootstrapFailed(true);
        return;
      }
      useMandateStore.setState((s) => {
        if (!s.transition) return s;
        const spaceId = boot.spaceId ?? s.transition.spaceId;
        const contextId = boot.contextId ?? s.transition.contextId;
        if (spaceId === s.transition.spaceId && contextId === s.transition.contextId) return s;
        return { transition: { ...s.transition, spaceId, contextId } };
      });
      if (boot.spaceId) {
        setBootstrapFailed(false);
        setMandateSpaceId(boot.spaceId);
        setIsReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [transitionId, userId]);

  const status: MandateBootstrapStatus = isReady
    ? 'ready'
    : transitionRead && !transition
      ? 'no-transition'
      : bootstrapFailed
        ? 'failed'
        : 'loading';

  return { isReady, mandateSpaceId, status };
}
